/**
 * md5 — minimal MD5 hex digest for client-side password hashing.
 *
 * Legacy `uzivatele.heslo` stores `MD5(plaintext)` and the new
 * `/v1/users` schemas accept a 32-char lowercase hex string. The Web
 * Crypto `SubtleCrypto.digest` API does not expose MD5, so we ship a
 * tiny self-contained implementation here.
 *
 * Adapted from the public-domain Joseph Myers / Paul Johnston pseudocode
 * (RFC 1321). Inputs are UTF-8 encoded before hashing.
 */

export function md5Hex(input: string): string {
  const bytes = new TextEncoder().encode(input);
  return rstrToHex(rawMd5(bytes));
}

function rstrToHex(bytes: Uint8Array): string {
  const hex = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i]!;
    out += hex[(b >>> 4) & 0x0f]! + hex[b & 0x0f]!;
  }
  return out;
}

function rawMd5(bytes: Uint8Array): Uint8Array {
  // Convert byte array → 32-bit little-endian word array, padded.
  const bitLen = bytes.length * 8;
  const padded = new Uint8Array(((bytes.length + 8) >>> 6) * 64 + 64);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  // Append bit length as 64-bit little-endian (low 32 only — message length
  // fits comfortably under 4 GiB for the password use case).
  const len = padded.length;
  padded[len - 8] = bitLen & 0xff;
  padded[len - 7] = (bitLen >>> 8) & 0xff;
  padded[len - 6] = (bitLen >>> 16) & 0xff;
  padded[len - 5] = (bitLen >>> 24) & 0xff;

  let a = 0x67452301;
  let b = 0xefcdab89;
  let c = 0x98badcfe;
  let d = 0x10325476;

  const x = new Int32Array(16);
  for (let i = 0; i < padded.length; i += 64) {
    for (let j = 0; j < 16; j++) {
      const o = i + j * 4;
      x[j] =
        (padded[o]! | (padded[o + 1]! << 8) | (padded[o + 2]! << 16) | (padded[o + 3]! << 24)) | 0;
    }

    const aa = a;
    const bb = b;
    const cc = c;
    const dd = d;

    a = ff(a, b, c, d, x[0]!, 7, -680876936);
    d = ff(d, a, b, c, x[1]!, 12, -389564586);
    c = ff(c, d, a, b, x[2]!, 17, 606105819);
    b = ff(b, c, d, a, x[3]!, 22, -1044525330);
    a = ff(a, b, c, d, x[4]!, 7, -176418897);
    d = ff(d, a, b, c, x[5]!, 12, 1200080426);
    c = ff(c, d, a, b, x[6]!, 17, -1473231341);
    b = ff(b, c, d, a, x[7]!, 22, -45705983);
    a = ff(a, b, c, d, x[8]!, 7, 1770035416);
    d = ff(d, a, b, c, x[9]!, 12, -1958414417);
    c = ff(c, d, a, b, x[10]!, 17, -42063);
    b = ff(b, c, d, a, x[11]!, 22, -1990404162);
    a = ff(a, b, c, d, x[12]!, 7, 1804603682);
    d = ff(d, a, b, c, x[13]!, 12, -40341101);
    c = ff(c, d, a, b, x[14]!, 17, -1502002290);
    b = ff(b, c, d, a, x[15]!, 22, 1236535329);

    a = gg(a, b, c, d, x[1]!, 5, -165796510);
    d = gg(d, a, b, c, x[6]!, 9, -1069501632);
    c = gg(c, d, a, b, x[11]!, 14, 643717713);
    b = gg(b, c, d, a, x[0]!, 20, -373897302);
    a = gg(a, b, c, d, x[5]!, 5, -701558691);
    d = gg(d, a, b, c, x[10]!, 9, 38016083);
    c = gg(c, d, a, b, x[15]!, 14, -660478335);
    b = gg(b, c, d, a, x[4]!, 20, -405537848);
    a = gg(a, b, c, d, x[9]!, 5, 568446438);
    d = gg(d, a, b, c, x[14]!, 9, -1019803690);
    c = gg(c, d, a, b, x[3]!, 14, -187363961);
    b = gg(b, c, d, a, x[8]!, 20, 1163531501);
    a = gg(a, b, c, d, x[13]!, 5, -1444681467);
    d = gg(d, a, b, c, x[2]!, 9, -51403784);
    c = gg(c, d, a, b, x[7]!, 14, 1735328473);
    b = gg(b, c, d, a, x[12]!, 20, -1926607734);

    a = hh(a, b, c, d, x[5]!, 4, -378558);
    d = hh(d, a, b, c, x[8]!, 11, -2022574463);
    c = hh(c, d, a, b, x[11]!, 16, 1839030562);
    b = hh(b, c, d, a, x[14]!, 23, -35309556);
    a = hh(a, b, c, d, x[1]!, 4, -1530992060);
    d = hh(d, a, b, c, x[4]!, 11, 1272893353);
    c = hh(c, d, a, b, x[7]!, 16, -155497632);
    b = hh(b, c, d, a, x[10]!, 23, -1094730640);
    a = hh(a, b, c, d, x[13]!, 4, 681279174);
    d = hh(d, a, b, c, x[0]!, 11, -358537222);
    c = hh(c, d, a, b, x[3]!, 16, -722521979);
    b = hh(b, c, d, a, x[6]!, 23, 76029189);
    a = hh(a, b, c, d, x[9]!, 4, -640364487);
    d = hh(d, a, b, c, x[12]!, 11, -421815835);
    c = hh(c, d, a, b, x[15]!, 16, 530742520);
    b = hh(b, c, d, a, x[2]!, 23, -995338651);

    a = ii(a, b, c, d, x[0]!, 6, -198630844);
    d = ii(d, a, b, c, x[7]!, 10, 1126891415);
    c = ii(c, d, a, b, x[14]!, 15, -1416354905);
    b = ii(b, c, d, a, x[5]!, 21, -57434055);
    a = ii(a, b, c, d, x[12]!, 6, 1700485571);
    d = ii(d, a, b, c, x[3]!, 10, -1894986606);
    c = ii(c, d, a, b, x[10]!, 15, -1051523);
    b = ii(b, c, d, a, x[1]!, 21, -2054922799);
    a = ii(a, b, c, d, x[8]!, 6, 1873313359);
    d = ii(d, a, b, c, x[15]!, 10, -30611744);
    c = ii(c, d, a, b, x[6]!, 15, -1560198380);
    b = ii(b, c, d, a, x[13]!, 21, 1309151649);
    a = ii(a, b, c, d, x[4]!, 6, -145523070);
    d = ii(d, a, b, c, x[11]!, 10, -1120210379);
    c = ii(c, d, a, b, x[2]!, 15, 718787259);
    b = ii(b, c, d, a, x[9]!, 21, -343485551);

    a = (a + aa) | 0;
    b = (b + bb) | 0;
    c = (c + cc) | 0;
    d = (d + dd) | 0;
  }

  const out = new Uint8Array(16);
  writeWord(out, 0, a);
  writeWord(out, 4, b);
  writeWord(out, 8, c);
  writeWord(out, 12, d);
  return out;
}

function writeWord(buf: Uint8Array, offset: number, value: number) {
  buf[offset] = value & 0xff;
  buf[offset + 1] = (value >>> 8) & 0xff;
  buf[offset + 2] = (value >>> 16) & 0xff;
  buf[offset + 3] = (value >>> 24) & 0xff;
}

function safeAdd(x: number, y: number): number {
  const lsw = (x & 0xffff) + (y & 0xffff);
  const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
  return (msw << 16) | (lsw & 0xffff);
}

function rol(num: number, count: number): number {
  return (num << count) | (num >>> (32 - count));
}

function cmn(q: number, a: number, b: number, x: number, s: number, t: number): number {
  return safeAdd(rol(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b);
}

function ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
  return cmn((b & c) | (~b & d), a, b, x, s, t);
}
function gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
  return cmn((b & d) | (c & ~d), a, b, x, s, t);
}
function hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
  return cmn(b ^ c ^ d, a, b, x, s, t);
}
function ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
  return cmn(c ^ (b | ~d), a, b, x, s, t);
}
